import React from 'react';
import { i18n } from '../../i18n/translate';
import { getActiveRuleSetFromUrl } from '../../utils/UrlUtil';
import * as Core from '../../core';

interface RuleSet {
  active?: boolean;
  interstitial_redirect?: string;
  name: string;
}

interface RoleOption {
  ruleSet: string;
  name: string;
  label: string;
  shortLabel: string;
}

interface SidebarRoleSwitcherProps {
  currentRuleSets?: RuleSet[];
  onMenuClose?: () => void;
  onMenuOpen?: () => void;
  onSelectRuleSet?: (ruleSetName: string) => void;
}

const ROLE_OPTIONS: RoleOption[] = [
  {
    ruleSet: 'cc_admin',
    name: 'cc_admin',
    label: i18n.t('Employees'),
    shortLabel: i18n.t('Employees'),
  },
  {
    ruleSet: 'scorm_manager',
    name: 'scorm_manager',
    label: i18n.t('Scorm'),
    shortLabel: i18n.t('Scorm'),
  },
];

const getSkipRoleSelectionValue = (): boolean => {
  if (typeof document !== 'undefined') {
    return !!document?.querySelector('[data-skip-role-selection="true"]');
  }
  return false;
};

export const SidebarRoleSwitcher: React.FC<SidebarRoleSwitcherProps> = ({
  currentRuleSets = [],
  onMenuClose,
  onMenuOpen,
  onSelectRuleSet,
}) => {
  const activeRuleSet = getActiveRuleSetFromUrl(currentRuleSets);
  const availableRuleSets = ROLE_OPTIONS.filter(roleOption =>
    currentRuleSets.some(currentRuleSet => currentRuleSet.name === roleOption.ruleSet)
  );
  const defaultRuleSet = availableRuleSets.find(roleOption => roleOption.ruleSet === activeRuleSet?.name);

  if (getSkipRoleSelectionValue() || (activeRuleSet?.name && !defaultRuleSet) || availableRuleSets.length < 2) {
    return null;
  }
  if (!defaultRuleSet) {
    return null;
  }

  const items: Core.Item[] = availableRuleSets.map(roleOption => ({
    ariaLabel: roleOption.label,
    text: roleOption.label,
    value: roleOption.ruleSet,
    dataAction: `select-role-${roleOption.ruleSet}`,
    dataObjectId: roleOption.name,
    dataObjectType: 'menu-dropdown-options',
  }));

  const onChange = (nextValue: string | string[]) => {
    const selectedRuleSet = availableRuleSets.find(roleOption => roleOption.ruleSet === nextValue);
    if (selectedRuleSet && onSelectRuleSet) onSelectRuleSet(selectedRuleSet.name);
  };

  return (
    <li className="sidebar-role-switcher" data-object-type="sidebar-role-switcher" data-action="sidebar-role-switcher">
      <Core.DropdownMenu
        aria-label={i18n.t('Switch role')}
        groups={[{ groupTitle: '', items, menuGroupOptionType: 'radio', value: defaultRuleSet.ruleSet, onChange }]}
        isLazy
        isMenuItemOptions
        menuButtonText={defaultRuleSet.shortLabel}
        menuButtonRightIcon={<Core.Icon iconName="angle-down" />}
        onClose={onMenuClose}
        onOpen={onMenuOpen}
        // the sidebar nav's overflow-x: hidden clips an inline menu
        usePortal
        variant="ghost"
        w="full"
      />
    </li>
  );
};

SidebarRoleSwitcher.displayName = 'SidebarRoleSwitcher';
